"use client"

import React, { createContext, useContext, useState, ReactNode } from 'react';

interface AIProvider {
    id: string;
    name: string;
    description: string;
    status: 'active' | 'inactive';
}

interface Service {
    id: string;
    name: string;
    providers: AIProvider[];
}

interface AIModelsContextType {
    activeProviders: Record<string, string>;
    services: Service[];
    toggleProvider: (serviceId: string, providerId: string) => void;
    getActiveProvider: (serviceId: string) => AIProvider | null;
    getActiveProviderName: (serviceId: string) => string;
}

const AIModelsContext = createContext<AIModelsContextType | undefined>(undefined);

const services: Service[] = [
    {
        id: "translate",
        name: "Translate",
        providers: [
            { id: "1", name: "Google", description: "Dịch văn bản sử dụng Google", status: "active" },
            { id: "2", name: "OpenAI", description: "Dịch văn bản sử dụng OpenAI", status: "inactive" },
        ]
    },
    {
        id: "tts",
        name: "Text to Speech",
        providers: [
            { id: "5", name: "Google", description: "Chuyển văn bản thành giọng nói sử dụng Google", status: "active" },
            { id: "6", name: "OpenAI", description: "Chuyển văn bản thành giọng nói sử dụng OpenAI", status: "inactive" },
        ]
    }
];

export const AIModelsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Set default active providers (Google for each service)
    const getDefaultActiveProviders = () => {
        const defaults: Record<string, string> = {};
        services.forEach(service => {
            const googleProvider = service.providers.find(p => p.name === 'Google');
            if (googleProvider) {
                defaults[service.id] = googleProvider.id;
            }
        });
        return defaults;
    };

    const [activeProviders, setActiveProviders] = useState<Record<string, string>>(getDefaultActiveProviders());

    const toggleProvider = (serviceId: string, providerId: string) => {
        setActiveProviders(prev => {
            const currentActive = prev[serviceId];
            if (currentActive === providerId) {
                // Nếu đang bật provider này, tắt nó
                const newState = { ...prev };
                delete newState[serviceId];
                return newState;
            } else {
                // Bật provider này và tắt các provider khác trong cùng service
                return {
                    ...prev,
                    [serviceId]: providerId
                };
            }
        });
    };

    const getActiveProvider = (serviceId: string): AIProvider | null => {
        const activeProviderId = activeProviders[serviceId];
        if (!activeProviderId) return null;

        const service = services.find(s => s.id === serviceId);
        return service?.providers.find(p => p.id === activeProviderId) || null;
    };

    const getActiveProviderName = (serviceId: string): string => {
        const provider = getActiveProvider(serviceId);
        return provider?.name.toLowerCase() || 'google'; // fallback to google
    };

    return (
        <AIModelsContext.Provider value={{
            activeProviders,
            services,
            toggleProvider,
            getActiveProvider,
            getActiveProviderName
        }}>
            {children}
        </AIModelsContext.Provider>
    );
};

export const useAIModels = () => {
    const context = useContext(AIModelsContext);
    if (context === undefined) {
        throw new Error('useAIModels must be used within an AIModelsProvider');
    }
    return context;
};